import * as React from 'react';
import { useHistory } from 'react-router';
import {
  useUpdateAccountMutation,
  useUpdateAccountPasswordMutation,
  useGetAccountLazyQuery,
} from '../generated/graphql';
import { Loading } from '../Loading';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { ToastContext } from '../components/Toast';
import { PasswordInput } from '../components/PasswordInput';
import { Button } from '../components/Button';
import { FormActions } from '../components/FormActions';
import { PageHeader } from '../components/PageHeader';
import { Input } from '../components/Input';
import { Authority, getAuthorityName } from '../components/AuthorityLabel';
import { useValidation } from '../utils/Validation';
import { ValidationRules } from '../utils/ValidationRules';
import './AdminCreateAccountPage.css';

/** ユーザー一覧のパス。キャンセルと保存の後にこの一覧へ戻る */
const LIST_PATH = '/admin/accounts';

const AUTHORITY_SELECT_ID = 'AdminCreateAccountPage-authority';

/** 権限の選択肢。スーパー管理者はこの画面では選べない */
const AUTHORITY_OPTIONS = [Authority.Normal, Authority.MasterAdmin, Authority.Admin];

interface AdminCreateAccountPageProps {
  existingId?: number;
}

interface AccountFormTexts {
  title: string;
  submitLabel: string;
  completedMessage: string;
  failedMessage: string;
}

/** 新規登録と編集で、見出し・送信ボタン・通知の文言を出し分ける */
function getFormTexts(isNew: boolean): AccountFormTexts {
  if (isNew) {
    return {
      title: 'ユーザーの登録',
      submitLabel: '登録',
      completedMessage: 'ユーザーを登録しました',
      failedMessage: 'ユーザーの登録に失敗しました',
    };
  }
  return {
    title: 'ユーザーの編集',
    submitLabel: '保存',
    completedMessage: 'ユーザーを保存しました',
    failedMessage: 'ユーザーの保存に失敗しました',
  };
}

/** 新規登録ではパスワードも検証する。編集ではパスワードを別の画面で変えるため、検証の対象から外す */
function getAccountRules(isNew: boolean) {
  if (isNew) {
    return ValidationRules.Account;
  }
  return {
    name: ValidationRules.Account.name,
    screenId: ValidationRules.Account.screenId,
    department: ValidationRules.Account.department,
  };
}

export const AdminCreateAccountPage: React.FC<AdminCreateAccountPageProps> = function (
  props
) {
  const isNew = !props.existingId;
  const texts = getFormTexts(isNew);

  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const validation = useValidation(getAccountRules(isNew));
  const [name, setName] = validation.useState('name', '');
  const [screenId, setScreenId] = validation.useState('screenId', '');
  const [department, setDepartment] = validation.useState('department', '');
  const [authLv, setAuthLv] = React.useState<number>(Authority.Normal);
  const [password, setPassword] = React.useState<string | undefined>(
    undefined
  );
  const [submitting, setSubmitting] = React.useState(false);
  // ユーザーを登録した直後にパスワードを設定するため、送信した時点のパスワードと登録されたユーザーのidを保持する
  const submittedPasswordRef = React.useRef<string>();
  const createdAccountIdRef = React.useRef<number>();

  const [updateAccountPassword] = useUpdateAccountPasswordMutation({
    onCompleted() {
      toastContext.showToast(texts.completedMessage, 'success');
      history.push(LIST_PATH);
    },
    onError() {
      // ユーザーは登録済みなので、同じ入力で送り直すと二重に登録される。パスワード変更画面へ移って設定し直してもらう
      toastContext.showToast(
        'ユーザーは登録しましたが、パスワードの設定に失敗しました。パスワード変更画面で設定してください',
        'error'
      );
      const createdAccountId = createdAccountIdRef.current;
      if (createdAccountId === undefined) {
        history.push(LIST_PATH);
        return;
      }
      history.push(`/admin/accounts/${createdAccountId}/password/edit`);
    },
  });
  const [updateAccount] = useUpdateAccountMutation({
    onCompleted(data) {
      if (!isNew) {
        toastContext.showToast(texts.completedMessage, 'success');
        history.push(LIST_PATH);
        return;
      }

      const submittedPassword = submittedPasswordRef.current;
      if (submittedPassword === undefined) {
        setSubmitting(false);
        return;
      }
      createdAccountIdRef.current = data.updateAccount.id;
      updateAccountPassword({
        variables: {
          input: {
            account_id: data.updateAccount.id,
            password: submittedPassword,
          },
        },
      });
    },
    onError() {
      toastContext.showToast(texts.failedMessage, 'error');
      setSubmitting(false);
    },
  });
  const [getAccount, getAccountResult] = useGetAccountLazyQuery({
    onCompleted(data) {
      const account = data.getAccount;
      setScreenId(account.screen_id);
      setName(account.name);
      setDepartment(account.department);
      setAuthLv(account.authority.level);
    },
  });

  if (props.existingId) {
    if (!getAccountResult.called) {
      getAccount({
        variables: {
          accountId: props.existingId,
        },
      });
    }

    if (getAccountResult.error) {
      return <GraphQLErrorDialog error={getAccountResult.error} />;
    }

    if (getAccountResult.loading || !getAccountResult.data) {
      return <Loading />;
    }
  }

  const submit = function () {
    submittedPasswordRef.current = password;
    setSubmitting(true);
    updateAccount({
      variables: {
        input: {
          id: props.existingId,
          screen_id: screenId,
          name: name,
          department: department,
          auth_lv: authLv,
        },
      },
    });
  };

  const isPasswordMissing = isNew && !password;
  const submitDisabled = validation.isInvalid || isPasswordMissing || submitting;

  let passwordField;
  if (isNew) {
    passwordField = (
      <PasswordInput
        validation={validation}
        onChange={(password, check) => {
          if (check) {
            setPassword(password);
          } else {
            setPassword(undefined);
          }
        }}
      />
    );
  } else {
    passwordField = (
      <div className="App-input_container">
        <span className="App-label">パスワード</span>
        {/* マイページのパスワードの行と同じく、設定済みであることを伏せ字で示し、その右に「変更」ボタンを置く */}
        <div className="AdminCreateAccountPage-password">
          <span className="AdminCreateAccountPage-password_mask" aria-label="設定済み">
            ••••••••
          </span>
          <Button
            type="button"
            color="white"
            mini={true}
            onClick={() =>
              history.push(`/admin/accounts/${props.existingId}/password/edit`)
            }
          >
            変更
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="AdminCreateAccountPage">
      <PageHeader
        title={texts.title}
        breadcrumbs={[
          { label: '管理画面' },
          { label: 'ユーザー一覧', to: LIST_PATH },
          { label: texts.title },
        ]}
      />
      <div className="AdminCreateAccountPage-form">
        <Input
          label="ID"
          type="text"
          required={true}
          onChange={(event) => {
            const screenId = event.target.value;
            setScreenId(screenId);
          }}
          value={screenId}
          validationMessage={validation.get('screenId')}
        />
        <Input
          label="名前"
          type="text"
          required={true}
          onChange={(event) => {
            const name = event.target.value;
            setName(name);
          }}
          value={name}
          validationMessage={validation.get('name')}
        />
        <Input
          label="所属部署"
          type="text"
          required={true}
          onChange={(event) => {
            const department = event.target.value;
            setDepartment(department);
          }}
          value={department}
          validationMessage={validation.get('department')}
        />
        <div className="App-input_container">
          <label className="App-label" htmlFor={AUTHORITY_SELECT_ID}>
            権限
            <span className="App-label_required" aria-hidden="true">
              *
            </span>
          </label>
          <select
            id={AUTHORITY_SELECT_ID}
            className="AdminCreateAccountPage-select"
            aria-required={true}
            onChange={(event) => setAuthLv(parseInt(event.target.value, 10))}
            value={authLv}
          >
            {AUTHORITY_OPTIONS.map((level) => (
              <option key={level} value={level}>
                {getAuthorityName(level)}
              </option>
            ))}
          </select>
        </div>
        {passwordField}
        <FormActions
          submitLabel={texts.submitLabel}
          submitDisabled={submitDisabled}
          onSubmit={submit}
          onCancel={() => history.push(LIST_PATH)}
        />
      </div>
    </div>
  );
};
